import { definePreset, palette } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';
import { bayhivePurple, bayhiveViolet } from './bayhive/shared.js';
import {
  blendColors,
  checkedText,
  contrastRatio,
  hexColor,
  readableBorder,
  readableColor,
  readableText,
} from './contrast.js';

const roles = ['primary', 'secondary', 'success', 'danger', 'warn', 'info'];
const hexRgb = (hex) => hex.slice(1).match(/../g).map((channel) => parseInt(channel, 16)).join(', ');
const rgbScale = (scale) =>
  Object.fromEntries(
    Object.entries(scale).map(([step, hex]) => [
      `${step}-rgb`,
      hex
        .slice(1)
        .match(/../g)
        .map((channel) => parseInt(channel, 16))
        .join(', '),
    ]),
  );
const colorsFor = (background, supplied) => ({
  color: hexColor(background),
  contrastColor: checkedText(background, supplied),
});

// Match stock BayHive's brighter hover in both modes, including custom hex colors.
export function primaryButtonStates(primary, mode) {
  const background = hexColor(primary);
  const dark = mode === 'dark';
  const stock = dark ? bayhiveViolet : bayhivePurple;
  const stockBase = stock[dark ? 300 : 700].toLowerCase();
  return {
    background,
    hover: background === stockBase
      ? stock[dark ? 200 : 500]
      : blendColors(background, '#ffffff', dark ? 0.2 : 0.16),
    active: background === stockBase
      ? stock[dark ? 100 : 600]
      : blendColors(background, '#ffffff', dark ? 0.3 : 0.08),
  };
}

// Low-saturation surfaces carry the selected brand without retaining BayHive purple.
export function brandSurfaceTokens(primary, mode) {
  const dark = mode === 'dark';
  const ramp = { 0: '#ffffff', ...Object.fromEntries(
    Object.entries(Lara.primitive.neutral).map(([step, color]) => [
      step, blendColors(color, primary, 0.05),
    ]),
  ) };
  const content = dark ? ramp[900] : ramp[0];
  const page = ramp[dark ? 950 : 50];
  const backgrounds = [content, page, ramp[dark ? 800 : 100]];
  return {
    ramp,
    content,
    page,
    contrastSurfaces: backgrounds,
    sidebar: ramp[950],
    text: readableColor(ramp[dark ? 50 : 900], backgrounds),
    muted: readableColor(ramp[dark ? 400 : 600], backgrounds),
    border: ramp[dark ? 700 : 200],
    nav: ramp[400],
  };
}

export function brandSidebarTokens(primary, accent, mode) {
  const surface = brandSurfaceTokens(primary, mode).sidebar;
  const focusBackground = blendColors(surface, primary, 0.12);
  const activeBackground = blendColors(surface, primary, 0.18);
  const icon = palette(accent)[200];
  return {
    focusBackground,
    activeBackground,
    activeIconColor: readableColor(icon, [focusBackground, activeBackground], 3),
    focusRingColor: readableColor(icon, [surface, focusBackground, activeBackground,
      blendColors(surface, '#ffffff', 0.12), blendColors(surface, '#ffffff', 0.2)], 3),
    focusRingWidth: '2px',
    focusRingShadow: 'none',
  };
}

// Legacy BayHive wrappers consume only the shadow; PrimeVue consumes the
// outline too. Keep both opaque and aligned on the same two-pixel edge.
const focusRing = (color) => ({ width: '2px', style: 'solid', color, offset: '0', shadow: `0 0 0 2px ${color}` });

// Shared by the real preset and settings preview so contrast checks match rendering.
export function secondaryButtonTokens(primary, mode, surface, contrastSurfaces = [surface]) {
  const dark = mode === 'dark';
  const background = blendColors(surface, primary, dark ? 0.12 : 0.16);
  const hoverBackground = blendColors(surface, primary, 0.28);
  const activeBackground = blendColors(surface, primary, 0.4);
  // Keep edges neutral instead of inheriting saturated primary shades.
  const edge = (amount) => readableBorder(
    blendColors(surface, readableText(surface), amount),
    contrastSurfaces,
  );
  return {
    background,
    hoverBackground,
    activeBackground,
    color: readableText(background),
    hoverColor: readableText(hoverBackground),
    activeColor: readableText(activeBackground),
    borderColor: edge(0.35),
    hoverBorderColor: edge(0.6),
    activeBorderColor: edge(0.75),
  };
}

// No options means no changes: the stock presets retain their exact defaults.
export function createBrandPreset(base, profile) {
  const overrides = { semantic: { colorScheme: {} }, components: {}, primitive: { bayhive: {} } };
  const schemes = profile.colors || {};
  for (const mode of ['light', 'dark']) {
    const values = schemes[mode];
    if (!values) continue;
    const semantic = {};
    const components = {};
    const dark = mode === 'dark';
    const primaryInput = typeof values.primary === 'string' ? values.primary : values.primary?.background;
    const brandSurfaces = profile.application === 'bayhive' && primaryInput
      ? brandSurfaceTokens(hexColor(primaryInput), mode)
      : null;
    if (brandSurfaces) {
      const { ramp, content, text, muted, border } = brandSurfaces;
      semantic.bayhive = { neutral: ramp };
      semantic.surface = ramp;
      semantic.text = { color: text, hoverColor: text, mutedColor: muted, hoverMutedColor: text };
      semantic.content = {
        background: content, hoverBackground: ramp[dark ? 800 : 100],
        borderColor: border, color: text, hoverColor: text,
      };
      const fieldBackground = dark ? ramp[950] : ramp[0];
      const fieldSurfaces = [content, brandSurfaces.page, fieldBackground, ramp[dark ? 900 : 100]];
      const fieldBorder = readableColor(primaryInput, fieldSurfaces, 3);
      semantic.formField = {
        background: fieldBackground,
        filledBackground: ramp[dark ? 950 : 50],
        filledHoverBackground: ramp[dark ? 900 : 100],
        filledFocusBackground: ramp[dark ? 900 : 100],
        borderColor: readableColor(ramp[500], fieldSurfaces, 3),
        hoverBorderColor: fieldBorder, focusBorderColor: fieldBorder,
        focusRing: focusRing(fieldBorder),
        color: text, placeholderColor: muted,
        floatLabelColor: muted, floatLabelActiveColor: muted, iconColor: muted,
        floatLabelFocusColor: readableColor(primaryInput, fieldSurfaces),
      };
      semantic.bayhive.sidebar = brandSidebarTokens(primaryInput, values.accent || primaryInput, mode);
    }
    if (values.accent) {
      const accent = hexColor(values.accent);
      const ramp = palette(accent);
      semantic.accent = { ...ramp, color: accent, contrastColor: readableText(accent) };
      // Existing BayHive brand-highlight consumers use the gold palette.
      semantic.bayhive = { ...semantic.bayhive, gold: { ...ramp, ...rgbScale(ramp) } };
    }

    for (const role of roles) {
      const input = values[role] ?? (role === 'info' ? values.primary : undefined);
      if (!input) continue;
      const options = typeof input === 'string' ? { background: input } : input;
      const state = colorsFor(options.background, options.text);
      const ramp = palette(state.color);
      const brandStates = profile.application === 'bayhive' && ['primary', 'info'].includes(role)
        ? primaryButtonStates(state.color, mode)
        : { hover: ramp[dark ? 300 : 600], active: ramp[dark ? 200 : 700] };
      const hover = options.hover ? hexColor(options.hover) : brandStates.hover;
      const active = options.active ? hexColor(options.active) : brandStates.active;
      const candidateText = dark ? ramp[200] : ramp[900];
      const surface = values.surface || values.contrastSurface || brandSurfaces?.content || (dark ? '#181c23' : '#ffffff');
      const roleSurfaces = brandSurfaces && !values.surface
        ? [surface, brandSurfaces.page, brandSurfaces.ramp[dark ? 800 : 100]] : [surface];
      const textColor =
        profile.application === 'bayhive' ? readableColor(candidateText, roleSurfaces)
          : contrastRatio(surface, candidateText) >= 4.5 ? candidateText : readableText(surface);
      semantic[role] = {
        ...ramp,
        ...rgbScale(ramp),
        ...state,
        contrast: { color: state.contrastColor },
        hoverColor: hover,
        activeColor: active,
        textColor,
        ...(profile.application === 'bayhive' ? {
          borderColor: readableColor(state.color, roleSurfaces, 3),
          focusRing: focusRing(readableColor(state.color, roleSurfaces, 3)),
          textRgb: hexRgb(textColor),
          colorRgb: hexRgb(state.color),
          contrastRgb: hexRgb(state.contrastColor),
          hoverContrastColor: readableText(hover), activeContrastColor: readableText(active),
          hoverContrastRgb: hexRgb(readableText(hover)), activeContrastRgb: hexRgb(readableText(active)),
          ...Object.fromEntries(Object.entries(ramp).map(([step, fill]) => [`${step}-contrast-color`, readableText(fill)])),
        } : {}),
        hover: { color: hover },
        active: { color: active },
      };
      const border = (fill) =>
        profile.application === 'bayhive' ? readableBorder(fill, roleSurfaces) : fill;
      const filled = {
        background: state.color,
        borderColor: border(state.color),
        color: state.contrastColor,
        hoverBackground: hover,
        hoverBorderColor: border(hover),
        hoverColor: readableText(hover),
        activeBackground: active,
        activeBorderColor: border(active),
        activeColor: readableText(active),
        focusRing: { color: textColor, shadow: profile.application === 'bayhive' ? focusRing(textColor).shadow : 'none' },
      };
      components.button ??= { root: {}, outlined: {}, text: {} };
      if (profile.application === 'bayhive') {
        components.button.root.focusRing = { width: '2px', style: 'solid', offset: '0' };
      }
      components.button.root[role] = filled;
      if (profile.application === 'bayhive' && role === 'primary' && !values.secondary) {
        components.button.root.secondary = {
          ...secondaryButtonTokens(state.color, mode, surface, roleSurfaces),
          focusRing: { color: textColor, shadow: focusRing(textColor).shadow },
        };
      }

      const hoverBackground = blendColors(surface, textColor, 0.08);
      const activeBackground = blendColors(surface, textColor, 0.12);
      const interactiveText = profile.application === 'bayhive'
        ? readableColor(textColor, [...roleSurfaces, hoverBackground, activeBackground]) : textColor;
      components.button.outlined[role] = {
        color: interactiveText,
        borderColor: interactiveText,
        hoverBackground,
        activeBackground,
      };
      components.button.text[role] = {
        color: interactiveText, hoverBackground, activeBackground,
      };
      for (const component of ['tag', 'badge']) {
        components[component] ??= {};
        components[component][role] = { background: state.color, color: state.contrastColor };
      }
      if (!['primary', 'secondary'].includes(role)) {
        const severity = role === 'danger' ? 'error' : role;
        const background = dark ? ramp[950] : ramp[50];
        const feedbackHover = blendColors(background, textColor, 0.08);
        const feedbackText = profile.application === 'bayhive'
          ? readableColor(textColor, [background, feedbackHover]) : checkedText(background, textColor);
        for (const component of ['message', 'toast']) {
          components[component] ??= {};
          components[component][severity] = {
            background,
            color: feedbackText,
            ...(profile.application === 'bayhive' ? {
              detailColor: feedbackText,
              closeButton: { hoverBackground: feedbackHover, focusRing: focusRing(feedbackText) },
            } : {}),
            borderColor: dark ? ramp[400] : ramp[700],
            outlined: { color: profile.application === 'bayhive' ? textColor : feedbackText,
              borderColor: profile.application === 'bayhive' ? textColor : feedbackText },
            simple: { color: profile.application === 'bayhive' ? textColor : feedbackText },
          };
        }
      }
      // Keep direct BayHive and legacy aliases aligned with the selected brand.
      const alias = role === 'primary' ? (dark ? 'violet' : 'purple') : role;
      overrides.primitive.bayhive[alias] = ramp;
      if (!dark) {
        for (const aliasName of {
          primary: ['purple'],
          secondary: [],
          success: ['green'],
          danger: ['red'],
          warn: ['yellow', 'orange', 'amber'],
          info: ['blue', 'sky'],
        }[role]) {
          overrides.primitive[aliasName] = ramp;
        }
      }
    }
    if (values.surface) {
      const surface = hexColor(values.surface);
      const text = checkedText(surface, values.text);
      const muted = checkedText(surface, values.mutedText || text);
      const hoverSurface = blendColors(surface, text, 0.06);
      const border = blendColors(surface, text, 0.3);
      semantic.content = {
        background: surface,
        hoverBackground: hoverSurface,
        borderColor: border,
        color: text,
        hoverColor: checkedText(hoverSurface),
      };
      semantic.text = { color: text, hoverColor: text, mutedColor: muted, hoverMutedColor: text };
      semantic.formField = {
        background: surface,
        color: text,
        placeholderColor: muted,
        borderColor: border,
        filledBackground: hoverSurface,
        filledHoverBackground: hoverSurface,
        filledFocusBackground: hoverSurface,
      };
      semantic.overlay = Object.fromEntries(
        ['modal', 'popover', 'select'].map((name) => [name, { background: surface, color: text }]),
      );
      semantic.surface = {
        0: '#ffffff',
        ...Object.fromEntries(
          [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950].map((step) => [
            step,
            blendColors(surface, text, dark ? (950 - step) / 1000 : step / 1100),
          ]),
        ),
      };
      // Shell surfaces use the same brand background instead of retaining purple.
      components.toolbar = { root: { background: hoverSurface, color: text, borderColor: border } };
      semantic.navigation = {
        item: { color: text, focusColor: text, focusBackground: hoverSurface },
      };
    }
    if (values.primary) {
      semantic.focusRing = { color: semantic.primary.textColor };
      semantic.highlight = {
        background: semantic.primary[dark ? 900 : 50],
        color: semantic.primary[dark ? 100 : 900],
      };
      if (brandSurfaces && !values.surface) {
        const { ramp, content } = brandSurfaces;
        const primary = semantic.primary.color;
        const surface = dark ? ramp[800] : content;
        const highlight = blendColors(surface, primary, dark ? 0.16 : 0.08);
        const highlightFocus = blendColors(surface, primary, dark ? 0.24 : 0.16);
        const highlightText = readableColor(semantic.primary.textColor, [highlight, highlightFocus]);
        semantic.focusRing = focusRing(semantic.primary.borderColor);
        semantic.highlight = { background: highlight, focusBackground: highlightFocus, color: highlightText, focusColor: highlightText };
        semantic.navigation = { item: {
          color: semantic.text.color,
          focusBackground: highlight, activeBackground: highlightFocus,
          focusColor: highlightText, activeColor: highlightText,
          icon: { color: semantic.text.mutedColor, focusColor: highlightText, activeColor: highlightText },
        } };
        const option = {
          color: semantic.text.color,
          focusBackground: highlight, selectedBackground: highlight, selectedFocusBackground: highlightFocus,
          focusColor: highlightText, selectedColor: highlightText, selectedFocusColor: highlightText,
        };
        semantic.list = { option };
        for (const name of ['select', 'autocomplete', 'multiselect']) components[name] = { option };
        const hover = semantic.primary.hoverColor;
        for (const name of ['checkbox', 'radiobutton']) components[name] = {
          root: {
            checkedBorderColor: semantic.primary.borderColor,
            checkedFocusBorderColor: semantic.primary.borderColor,
            checkedHoverBorderColor: readableColor(hover, [content, surface], 3),
          },
          icon: { checkedColor: readableText(primary), checkedHoverColor: readableText(hover) },
        };
        const switchBackground = ramp[dark ? 700 : 300];
        const switchHover = ramp[dark ? 600 : 400];
        components.toggleswitch = {
          root: {
            borderColor: readableBorder(switchBackground, content),
            hoverBorderColor: readableBorder(switchHover, content),
            checkedBorderColor: semantic.primary.borderColor,
            checkedHoverBorderColor: readableColor(hover, [content, surface], 3),
          },
          handle: {
            background: readableText(switchBackground), hoverBackground: readableText(switchHover),
            checkedBackground: readableText(primary), checkedHoverBackground: readableText(hover),
            checkedColor: primary, checkedHoverColor: hover,
          },
        };
        // Table focus sits inside the header or row, so check its ring against
        // their real fills instead of inheriting an input-field shadow.
        const headerBackground = ramp[dark ? 700 : 100];
        const headerHover = dark
          ? blendColors(headerBackground, semantic.primary[600], 0.22) : ramp[200];
        const headerSelected = ramp[dark ? 800 : 200];
        const insetRing = (backgrounds) => ({
          width: '2px', style: 'solid', offset: '-2px', shadow: 'none',
          color: readableColor(primary, backgrounds, 3),
        });
        components.datatable = {
          headerCell: {
            focusRing: insetRing([headerBackground, headerHover, headerSelected]),
          },
          row: {
            focusRing: insetRing([content, ramp[dark ? 950 : 50], ramp[dark ? 800 : 100], highlight]),
          },
        };
        const chip = semantic.primary[dark ? 300 : 700];
        const chipFocus = semantic.primary[dark ? 200 : 800];
        semantic.bayhive.autocomplete = { chip: {
          background: chip, color: readableText(chip),
          focusBackground: chipFocus, focusColor: readableText(chipFocus),
          focusRingColor: readableText(chipFocus),
        } };
      }
    }
    overrides.semantic.colorScheme[mode] = semantic;
    for (const [name, tokens] of Object.entries(components)) {
      overrides.components[name] ??= { colorScheme: {} };
      overrides.components[name].colorScheme[mode] = tokens;
    }
  }
  if (profile.fontFamily) overrides.semantic.typography = { fontFamily: profile.fontFamily };
  if (profile.radius) {
    const { control, surface, dialog } = profile.radius;
    if (control) overrides.semantic.formField = { borderRadius: control };
    if (surface) {
      overrides.semantic.content = { borderRadius: surface };
      overrides.components.card = { root: { borderRadius: surface } };
      overrides.components.panel = { header: { borderRadius: `${surface} ${surface} 0 0` } };
    }
    if (dialog) overrides.semantic.overlay = { modal: { borderRadius: dialog } };
  }
  // Advanced PrimeVue tokens remain available without changing the factory.
  const result = definePreset(base, overrides, profile.tokens || {});
  if (profile.fontFamily) {
    const baseCss = result.css;
    result.css = (context) => `${typeof baseCss === 'function' ? baseCss(context) : baseCss || ''}
html[data-ui-theme='${profile.id}'], html[data-ui-theme='${profile.id}'] body {
  font-family: var(--p-typography-font-family);
}`;
  }
  return result;
}
